/**
 * Centralna mapa route'ów z lokalizowanymi ścieżkami.
 *
 * Klucz  → camelCase identyfikator route'a
 * Wartość → { en: ścieżka angielska, pl: ścieżka polska }
 *
 * Parametry dynamiczne: :id, :slug, :code
 */
export const ROUTE_MAP = {
    // === Public ===
    login:              { en: "/login",                pl: "/logowanie" },
    // [HASLA-LOKALNE 2026-09-27] Zmiana hasła konta lokalnego — wymuszona (hasło tymczasowe / po 45 dniach) i z linku
    // przypomnienia. Poza układem aplikacji; App.js rejestruje OBIE ścieżki literalnie (link z maila jest po polsku).
    zmianaHasla:        { en: "/change-password",      pl: "/zmiana-hasla" },
    acceptance:         { en: "/acceptance/:code",     pl: "/akceptacja/:code" },
    // [polecenie właściciela 2026-09-14] Publiczne szkolenie z linku (bez logowania, kod dostępu per osoba).
    // Parametr nazywa się :code (token linku), bo zbiór dozwolonych nazw parametrów jest zamrożony w routes.test.js.
    publicTraining:     { en: "/training/:code",        pl: "/szkolenie/:code" },
    // [2026-09-14] Podpis online umowy — link z e-maila (bez logowania, poza Layout). Backend buduje link
    // ZAWSZE po polsku (/podpis/{token}), dlatego App.js rejestruje OBIE ścieżki literalnie.
    podpisOnline:       { en: "/sign/:token",          pl: "/podpis/:token" },
    // [polecenie właściciela 2026-09-20] Dokumenty prawne platformy — publiczne (bez logowania),
    // linkowane ze stopki aplikacji I ze strony logowania. Trzy STATYCZNE klucze zamiast parametru:
    // zbiór nazw parametrów tras jest zamrożony w routes.test.js, a lista dokumentów jest zamknięta.
    dokumentRegulamin:  { en: "/legal/terms",          pl: "/dokumenty/regulamin" },
    dokumentRodo:       { en: "/legal/privacy",        pl: "/dokumenty/rodo" },
    dokumentCookies:    { en: "/legal/cookies",        pl: "/dokumenty/cookies" },

    // === Dashboard ===
    dashboard:          { en: "/dashboard",            pl: "/pulpit" },

    // === Moje zgłoszenia ===
    myTickets:          { en: "/my-tickets",           pl: "/moje-zgloszenia" },
    myTicketDetail:     { en: "/my-tickets/:id",       pl: "/moje-zgloszenia/:id" },

    // === Treści ===
    news:               { en: "/news",                 pl: "/aktualnosci" },
    newsDetail:         { en: "/news/:slug",           pl: "/aktualnosci/:slug" },
    gallery:            { en: "/gallery",              pl: "/galeria" },
    myAnnouncements:    { en: "/my-announcements",     pl: "/moje-komunikaty" },
    contests:           { en: "/contests",             pl: "/konkursy" },
    contestDetail:      { en: "/contests/:slug",       pl: "/konkursy/:slug" },
    documents:          { en: "/documents",            pl: "/dokumenty" },
    // [POLITYKI 2026-09-28, E7 §6] Polityki i procedury: zarządzanie (/polityki — PolitykiRedakcja i klucze rejestracji),
    // raport stanowisk, widok obszarów oraz WŁASNA trasa obowiązku /do-potwierdzenia (D31: link powiadomień poza sitem
    // NotificationLinkScope — obowiązek ma dojść do osoby z ukrytym modułem „Dokumenty”).
    // [2026-10-01, projekt „Cykl życia na pasku”] polityki / politykiStanowiska / politykiObszary zostają w mapie, bo stoją w linkach
    // powiadomień zapisanych w bazie — App.js renderuje pod nimi wyłącznie PRZEKIEROWANIE (PrzekierowaniePolityk) na kafelek
    // /admin/procedury-bezpieczenstwo (z kluczem), do Mój Dział → Procedury, do „Do potwierdzenia” albo — u właściciela procedury bez
    // klucza i bez Mojego Działu — otwiera okno procedury na miejscu.
    polityki:           { en: "/policies",             pl: "/polityki" },
    politykiStanowiska: { en: "/policies/positions",   pl: "/polityki/stanowiska" },
    politykiObszary:    { en: "/policies/areas",       pl: "/polityki/obszary" },
    doPotwierdzenia:    { en: "/to-acknowledge",       pl: "/do-potwierdzenia" },
    // [POLITYKI ZATWIERDZANIE 2026-09-29, E7 D23/D27] „Do decyzji” (członek zarządu / recenzent — prawo z rundy),
    // rejestracja decyzji z zewnątrz / inwentaryzacja / głos w imieniu (klucz PolitykiRejestrujDecyzje), karta decyzji do druku.
    politykiDoDecyzji:  { en: "/policies/decisions",   pl: "/polityki/do-decyzji" },
    politykiRejestracja: { en: "/policies/register-decision", pl: "/polityki/rejestracja-decyzji" },
    politykiKartaDecyzji: { en: "/policies/decision-card/:id", pl: "/polityki/karta-decyzji/:id" },
    // [E8 2026-09-30, §7 / D43 / D44] Wykaz KSC — monitor wpisu: WŁASNE trasy (link powiadomień `/wykaz-ksc` poza sitem
    // NotificationLinkScope — lekcja D31), moduł widoczności `wykazksc`, klucze need-to-know WykazKscPodglad / Redakcja / Atestacja.
    wykazKsc:           { en: "/ksc-registry",         pl: "/wykaz-ksc" },
    wykazKscSpolka:     { en: "/ksc-registry/company/:id", pl: "/wykaz-ksc/spolka/:id" },
    wykazKscSprawy:     { en: "/ksc-registry/cases",   pl: "/wykaz-ksc/sprawy" },
    wykazKscZasoby:     { en: "/ksc-registry/assets",  pl: "/wykaz-ksc/zasoby" },
    wykazKscUmowy:      { en: "/ksc-registry/contracts", pl: "/wykaz-ksc/umowy" },
    wykazKscZrodla:     { en: "/ksc-registry/sources", pl: "/wykaz-ksc/zrodla" },
    wykazKscPrawo:      { en: "/ksc-registry/law",     pl: "/wykaz-ksc/prawo" },
    eLearning:          { en: "/elearning",            pl: "/szkolenia" },
    eLearningDetail:    { en: "/elearning/:id",        pl: "/szkolenia/:id" },
    eLearningLearn:     { en: "/elearning/:id/learn",  pl: "/szkolenia/:id/nauka" },
    eLearningQuiz:      { en: "/elearning/:id/quiz",   pl: "/szkolenia/:id/quiz" },
    surveys:            { en: "/surveys",              pl: "/ankiety" },
    surveyFill:         { en: "/surveys/:id",          pl: "/ankiety/:id" },
    // [PAKIET 2026-09-02] Oferty pracy (J) — treść dla pracownika, jak konkursy.
    // Kod modułu w menu i w ProtectedRoute jest ten sam co klucz trasy („jobOffers") — świadomie,
    // żeby nie powtórzyć rozjazdu eLearning/elearning.
    jobOffers:          { en: "/job-offers",           pl: "/oferty-pracy" },
    jobOfferDetail:     { en: "/job-offers/:slug",     pl: "/oferty-pracy/:slug" },
    notificationCenter: { en: "/notification-center",  pl: "/centrum-powiadomien" },

    // === Narzędzia ===
    contactForm:        { en: "/contact-form",         pl: "/formularz-kontaktowy" },
    addressBook:        { en: "/address-book",         pl: "/ksiazka-adresowa" },
    knowledgeBase:      { en: "/knowledge-base",       pl: "/baza-wiedzy" },
    /* [polecenie właściciela 2026-09-30, G] „link ma być tematem” — artykuł pod adresem z TEMATU (slug), nie z numeru.
       Stary adres liczbowy rozpoznaje strona artykułu (parametr może być liczbą) i przekierowuje na temat. */
    knowledgeBaseArticle: { en: "/knowledge-base/:slug", pl: "/baza-wiedzy/:slug" },
    sap:                { en: "/sap",                  pl: "/sap" },
    calendar:           { en: "/calendar",             pl: "/kalendarz" },
    kanban:             { en: "/kanban",               pl: "/kanban" },
    tasks:              { en: "/tasks",                pl: "/zadania" },
    teams:              { en: "/teams",                pl: "/teams" },
    // [STOPKI] Sekcja pracownika przeniesiona z samodzielnej aplikacji app/stopka.
    mySignature: { en: "/my-signature", pl: "/moja-stopka" },
    myFiles:            { en: "/my-files",             pl: "/moje-pliki" },
    /* [BC-11 2026-09-18] „Kto wchodzil na moje konto" — lista sesji trybu „zaloguj jako" NA WLASNYM
       koncie. Sciezka PL musi byc zgodna z linkiem powiadomienia (PodgladKontaSesjeService.LinkListy),
       bo dzwonek przenosi wprost pod ten adres. */
    podgladMojegoKonta: { en: "/account-preview-log", pl: "/podglad-mojego-konta" },
    // [F-8 2026-09-11] Menedzer hasel pracownika (sejf osobisty + banki dzialowe i modulowe).
    sejf:               { en: "/tools/vault",          pl: "/narzedzia/sejf" },
    sygnalista:         { en: "/whistleblower",       pl: "/sygnalista" },
    projects:           { en: "/projects",             pl: "/projekty" },
    projectDetail:      { en: "/projects/:id",         pl: "/projekty/:id" },
    // [P9] Witryna GRUPY projektów (mega-projekt). Powiadomienie pl_group_added linkuje
    // wprost do /projekty/grupa/{id}, więc wzorzec PL musi brzmieć dokładnie tak
    // (segment statyczny „grupa" wygrywa w routerze z parametrem :id trasy projectDetail).
    projectGroup:       { en: "/projects/group/:id",   pl: "/projekty/grupa/:id" },

    // === Jury ===
    jury:               { en: "/jury",                 pl: "/jury" },
    juryDetail:         { en: "/jury/:id",             pl: "/jury/:id" },

    // === Zgłoszenia (obsługa) ===
    // [2026-07-27] WZORZEC ADRESÓW MODUŁÓW OBSŁUGI: „obsluga-<dział>/<podstrona>".
    // Lista zgłoszeń jest podstroną obsługi IT (jak wnioski są podstroną obsługi HR:
    // /obsluga-hr/wnioski), a nie osobną gałęzią /zgloszenia. Nowe podstrony modułów
    // ticketowych dokładamy TĄ SAMĄ ścieżką, żeby drzewo adresów odpowiadało drzewu obsługi.
    // Stare adresy (/zgloszenia, /tickets) przekierowują — patrz LEGACY_REDIRECTS w App.js.
    tickets:            { en: "/it-management/tickets", pl: "/obsluga-it/zgloszenia" },
    ticketDetail:       { en: "/it-management/tickets/:id", pl: "/obsluga-it/zgloszenia/:id" },
    // [A15-15] BYŁO ZLE: klucze ticketStats (/statystyki-zgloszen) i analytics (/analityka) zostały
    // w mapie po przeniesieniu analityki do panelu admina — nie miały <Route> w App.js i nic do nich
    // nie linkowało. SKUTEK DLA UŻYTKOWNIKA: wpisanie tych adresów z ręki albo ze starej zakładki
    // kończyło się cichym wyrzuceniem na pulpit, bez słowa wyjaśnienia dokąd te strony poszły.
    // NAPRAWA: klucze usunięte — mapa tras ma opisywać to, co w aplikacji naprawdę istnieje.
    // Analityka żyje jako kafelek panelu admina (slug 'analytics' w adminTilesConfig).

    // === ITAM ===
    itamOverview:       { en: "/itam/overview",        pl: "/itam/przeglad" },
    itamDevices:        { en: "/itam/devices",         pl: "/itam/urzadzenia" },
    itamInvoices:       { en: "/itam/invoices",        pl: "/itam/faktury" },
    itamProtocols:      { en: "/itam/protocols",       pl: "/itam/protokoly" },
    itamLicenses:       { en: "/itam/licenses",        pl: "/itam/licencje" },
    itamTeamviewer:     { en: "/itam/teamviewer",      pl: "/itam/teamviewer" },
    // [polecenie właściciela 2026-09-15] Karty SIM przeniesione z /admin/karty-sim do sekcji ITAM (stary adres przekierowuje).
    itamSim:            { en: "/itam/sim-cards",       pl: "/itam/karty-sim" },

    // === Wnioski rejestracji ===
    /* [OBSZAR B 2026-09-03, poz. 3c] BYŁO ŹLE: trasa stała w App.js LITERALNIE („/wnioski-rejestracji"
       i „/registration-requests"), ale nie było jej w tej mapie. Skutki były dwa, oba ciche:
       (1) zapadka LinkiPowiadomienIstniejaWMapieTrasTests liczy „żywe adresy" właśnie stąd, więc link
           powiadomienia RegistrationService wyglądał na martwy, choć działał — a prawdziwie martwy
           obok niego mógł się przemknąć;
       (2) przełącznik języka (translateCurrentPath) nie umiał przetłumaczyć tego adresu, więc zmiana
           języka na tej stronie wyrzucała człowieka na pulpit.
       Klucz jest zarejestrowany literalnie — patrz REGISTERED_LITERALLY w routes.test.js, gdzie
       obecność OBU ścieżek w App.js jest sprawdzana, a nie deklarowana. */
    registrationRequests: { en: "/registration-requests", pl: "/wnioski-rejestracji" },

    // === Onboarding ===
    onboarding:         { en: "/onboarding",           pl: "/onboarding" },
    onboardingRequests: { en: "/onboarding-requests",  pl: "/wnioski-onboarding" },

    // === Sprawy pracownicze (HR — wszyscy zalogowani) ===
    hrRequests:         { en: "/hr-requests",          pl: "/wnioski-hr" },
    hrMyWorkTime:       { en: "/my-work-time",         pl: "/moj-czas-pracy" },
    hrMyDocuments:      { en: "/my-hr-documents",      pl: "/moje-dokumenty-hr" },
    remoteWorkReport:   { en: "/remote-work-report",   pl: "/rozliczenie-pracy-zdalnej" },
    // [PAKIET 2026-09-02] Ścieżka kariery (G) i cele (H) — sekcja osobista pracownika.
    // Kody modułów w menu: „careerPath" i „goals" (mainMenuModules.js) — celowo INNE niż klucz
    // trasy, jak eLearning/elearning: klucz opisuje adres, kod opisuje moduł do ukrycia.
    hrMyCareerPath:     { en: "/my-career-path",       pl: "/moja-sciezka-kariery" },
    myGoals:            { en: "/my-goals",             pl: "/moje-cele" },
    // [A15-15] BYŁO ZLE: klucz myVacationPlan (/moj-plan-urlopow) nie miał <Route> w App.js i nic
    // do niego nie linkowało. SKUTEK DLA UŻYTKOWNIKA: adres obiecywał plan urlopów, a przenosił
    // na pulpit bez komunikatu. NAPRAWA: klucz usunięty. Plan urlopów przełożonego żyje pod
    // myDeptVacationPlan (/moj-dzial/plan-urlopow) i tam kierują wszystkie odnośniki.

    // === Mój Dział (przełożeni — jobPosition >= 30) ===
    myDeptOverview:     { en: "/my-dept/overview",     pl: "/moj-dzial/pulpit" },
    myDeptRequests:     { en: "/my-dept/requests",     pl: "/moj-dzial/wnioski" },
    myDeptLeave:        { en: "/my-dept/leave",        pl: "/moj-dzial/urlopy" },
    myDeptVacationPlan: { en: "/my-dept/vacation-plan", pl: "/moj-dzial/plan-urlopow" },
    myDeptSchedule:     { en: "/my-dept/schedule",     pl: "/moj-dzial/grafik" },
    myDeptStructure:    { en: "/my-dept/structure",    pl: "/moj-dzial/struktura" },
    myDeptRemote:       { en: "/my-dept/remote",       pl: "/moj-dzial/praca-zdalna" },
    myDeptKanban:       { en: "/my-dept/kanban",       pl: "/moj-dzial/kanban" },
    myDeptTodo:         { en: "/my-dept/todo",          pl: "/moj-dzial/todo" },
    myDeptPermissions:  { en: "/my-dept/permissions",   pl: "/moj-dzial/uprawnienia" },
    myDeptEquipment:    { en: "/my-dept/equipment",    pl: "/moj-dzial/wyposazenie" },
    // [F-8 2026-09-11] „sejf przelozonego w /moj-dzial" — ten sam bank hasel dzialu,
    // pokazany tam, gdzie przelozony pracuje z zespolem.
    myDeptSejf:         { en: "/my-dept/vault",        pl: "/moj-dzial/sejf" },
    myDeptManagerReqs:  { en: "/my-dept/manager-requests", pl: "/moj-dzial/wnioski-przelozonego" },
    myDeptELearning:    { en: "/my-dept/elearning",    pl: "/moj-dzial/e-learning" },
    myDeptEvaluation:   { en: "/my-dept/evaluation",   pl: "/moj-dzial/ocena-pracownika" },
    myDeptWorkTime:     { en: "/my-dept/worktime",     pl: "/moj-dzial/czas-pracy" },
    // [PAKIET 2026-09-02] Dwie nowe zakładki hubu: cele zespołu (H) i dokumenty działu (K).
    // Ostatni segment PL MUSI być równy `id` zakładki w config/myDeptTabs.js — na tym stoi
    // wykrywanie aktywnej zakładki (routes.test.js pilnuje obu stron tej równości).
    myDeptGoals:        { en: "/my-dept/goals",        pl: "/moj-dzial/cele" },
    myDeptDocuments:    { en: "/my-dept/documents",    pl: "/moj-dzial/dokumenty" },
    myDeptProcedury:    { en: "/my-dept/procedures",   pl: "/moj-dzial/procedury" },

    // === Obsługa IT (serwisanci — TicketView) ===
    itManagement:       { en: "/it-management",        pl: "/obsluga-it" },
    // [zgłoszenie właściciela 2026-09-11] Hasła modułu obsługi — wejście z landingu, jeden poziom
    // wyżej niż sejf działu i jeden niżej niż wgląd centralny w panelu.
    itSejf:             { en: "/it-management/vault",  pl: "/obsluga-it/sejf" },
    // [F14] Landing per moduł zgłoszeń — wpis w ROUTE_MAP, by translateCurrentPath i LocalizedRedirect
    // znały trasę (przełączenie języka / deep-link nie wyrzuca na dashboard).
    itManagementModule: { en: "/it-management/:moduleCode", pl: "/obsluga-it/:moduleCode" },
    /* [zgłoszenie właściciela 2026-09-11 wieczór] Sejf modułu DYNAMICZNEGO. Dwa moduły wbudowane
       (itsm, hr) mają własne, statyczne adresy wyżej; każdy moduł założony kreatorem desku dostaje
       swój sejf tutaj, a kod modułu przychodzi PARAMETREM TRASY. Bez tego landing modułu
       dynamicznego prowadziłby kafelkiem „Hasła modułu" do sejfu CUDZEGO modułu — czyli do tego
       samego nieporozumienia, które właściciel zgłosił dla /obsluga-hr/sejf.
       Adres statyczny `/obsluga-it/sejf` wygrywa z tym wzorcem, bo React Router stawia segment
       stały ponad parametrem — kolejność deklaracji nie ma tu znaczenia. */
    itModuleSejf:       { en: "/it-management/:moduleCode/vault", pl: "/obsluga-it/:moduleCode/sejf" },
    /* [polecenie właściciela 2026-09-30, E2] PROCEDURY MODUŁU I „TWÓJ ZAKRES PRACY” — kafelek `mod_procedury`.
       Trzy adresy jednej strony (wzorzec sejfu): moduł IT wbudowany, moduł dynamiczny (kod w parametrze trasy)
       i moduł kadr. Adres statyczny wygrywa z wzorcem `:moduleCode` (segment stały ponad parametrem). */
    itProcedury:        { en: "/it-management/procedures", pl: "/obsluga-it/procedury" },
    itModuleProcedury:  { en: "/it-management/:moduleCode/procedures", pl: "/obsluga-it/:moduleCode/procedury" },

    // === Obsługa wniosków HR (kadrowcy — HrAccess) ===
    hrManagement:       { en: "/hr-management",        pl: "/obsluga-hr" },
    hrSejf:             { en: "/hr-management/vault",  pl: "/obsluga-hr/sejf" },
    hrProcedury:        { en: "/hr-management/procedures", pl: "/obsluga-hr/procedury" },
    hrFullView:         { en: "/hr-management/requests", pl: "/obsluga-hr/wnioski" },
    // [DELEGACJE 2026-09-12] Kolejka rozliczeń delegacji dla kadr i księgowości (HrAccess ALBO HrAccounting).
    hrRozliczenia:      { en: "/hr-management/settlements", pl: "/obsluga-hr/rozliczenia" },
    // [R1 2026-09-01] Kolejka ZGŁOSZEŃ modułu „Obsługa HR" pod WŁASNYM adresem modułu.
    // Właściciel: „zgłoszenia HR są w sekcji obsluga-hr/zgloszenia". Wcześniej jedyną drogą był
    // adres cudzego modułu z parametrem (/obsluga-it/hr?widok=zgloszenia) — patrz
    // VIEW_MODULE_QUEUE_ROUTES niżej; stary adres przekierowuje tutaj (ItManagementPage).
    hrTickets:          { en: "/hr-management/tickets",  pl: "/obsluga-hr/zgloszenia" },
    hrSygnalista:       { en: "/hr-management/whistleblower", pl: "/obsluga-hr/sygnalista" },
    hrSchedules:        { en: "/hr-management/schedules",     pl: "/obsluga-hr/grafiki" },
    hrRemoteReports:    { en: "/hr-management/remote-reports", pl: "/obsluga-hr/raport-pracy-zdalnej" },
    /* [PAKIET 2026-09-02] Podstrony obsługi HR — wzorzec „obsluga-<dział>/<podstrona>" (jak
       /obsluga-hr/wnioski). Trasy są RODZEŃSTWEM /obsluga-hr, więc nie przesłaniają landingu.
       `hrIssuedDocs` (rejestr dokumentów wydanych, projekt I-a) CELOWO NIE POWSTAJE: fundament
       §1.6 scalił rejestr zatrudnienia z rejestrem dokumentów działu w JEDNĄ stronę panelu
       /admin/issued-documents (kafelek `hr_issued_documents`), a dwa adresy do jednego rejestru
       to dokładnie ten dubel, który ten pakiet miał wyeliminować. */
    hrCareerPaths:      { en: "/hr-management/career-paths",   pl: "/obsluga-hr/sciezki-kariery" },
    hrGoals:            { en: "/hr-management/goals",          pl: "/obsluga-hr/cele" },
    hrOnboarding:       { en: "/hr-management/onboarding",     pl: "/obsluga-hr/onboarding" },
    hrOnboardingCase:   { en: "/hr-management/onboarding/:id", pl: "/obsluga-hr/onboarding/:id" },

    // === E-Teczka (HR) ===
    eTeczka:            { en: "/e-teczka",            pl: "/e-teczka" },

    /* === Struktura organizacyjna — EDYTOR, sekcja administracyjna ===
       [polecenie właściciela 2026-09-22] „sekcje /struktura-organizacyjna przenieś do
        /admin/struktura-organizacyjna — zrób dla tego kafelek i ma to być dostępne w sekcji admin,
        z access-control wyłącz widget wyłączania dla /struktura-organizacyjna — to uprawnienia
        administracyjne mają być, jest zła struktura linku."

       Adres modułu przestał być adresem pracownika: edytor schematu (przenoszenie ludzi między
       działami, piony, szkice reorganizacji) należy do administracji, a nie do menu pracownika.
       Wejście rozstrzyga KLUCZ (`OrgStructureView`), nie widoczność modułu — dlatego trasa nie ma
       już `restrictedModule`, a kod „orgStructure" zniknął ze słownika widoczności menu.

       ⚠️ OGLĄDANIE schematu ZOSTAJE tam, gdzie było: zakładka „Struktura" w /ksiazka-adresowa
       (components/AddressBook/StructureView.js) i ona klucza administracyjnego NIE wymaga.
       Przenosiny dotyczą EDYTORA, nie podglądu. */
    orgStructure:       { en: "/admin/org-structure", pl: "/admin/struktura-organizacyjna" },
    /* STARY ADRES ŻYJE JAKO PRZEKIEROWANIE. Ludzie mają go w zakładkach przeglądarki, a serwer
       wstawia go do maili o decyzji Zarządu (`OrgDivisionService` → „Otwórz strukturę organizacyjną").
       Martwy link to regresja, więc klucz zostaje w mapie wyłącznie po to, żeby App.js mógł
       zarejestrować pod nim <Navigate> na nowy adres. Nic innego go nie używa. */
    orgStructureLegacy: { en: "/org-structure",       pl: "/struktura-organizacyjna" },
    // [audyt 2026-09-22] Decyzja Zarządu o szkicu struktury. OSOBNY adres, bo mail z prośbą o zgodę
    // idzie do członków Zarządu, a ci NIE mają klucza `OrgStructureView` — link na trasę modułu
    // odbijał się od bramki uprawnień. Tutaj o dostępie rozstrzygają dane (serwer oddaje szkice
    // wyłącznie adresatowi rundy), a nie bramka trasy.
    orgDecision:        { en: "/structure-decision",  pl: "/decyzja-struktury" },

    // === Admin ===
    admin:              { en: "/admin",                pl: "/admin" },
    adminModule:        { en: "/admin/:moduleSlug",    pl: "/admin/:moduleSlug" },
    adminAnnouncements: { en: "/admin/announcements",  pl: "/admin/komunikaty" },
    adminEmployees:     { en: "/admin/employees",      pl: "/admin/pracownicy" },
    adminEmployeeWorkTime: { en: "/admin/employees/:userId/work-time", pl: "/admin/pracownicy/:userId/czas-pracy" },
    // [BE-6b 2026-09-19] Kolejka kadr: zmiany danych kadrowych do przejrzenia („HR ma to wiedziec i ma miec status z tego").
    adminZmianyKadrowe: { en: "/admin/hr-changes",     pl: "/admin/zmiany-kadrowe" },
    adminContactForms:  { en: "/admin/contact-forms",  pl: "/admin/formularze-kontaktowe" },
    // [J1] Narzedzie migracji schematu bazy — wlasna trasa, bo kafelek panelu nalezy do rownoleglej fali.
    adminDbMigration:   { en: "/admin/db-migration",   pl: "/admin/migracja-bazy" },
};

/**
 * [R1 2026-09-01] REGUŁA: moduł zgłoszeń ze STRONĄ WBUDOWANĄ ma kolejkę pod SWOIM adresem.
 *
 * Klucz  → `TicketModule.ViewModuleCode` (kod strony wbudowanej, np. „hrManagement" = /obsluga-hr)
 * Wartość → klucz ROUTE_MAP trasy z KOLEJKĄ ZGŁOSZEŃ tego modułu (np. /obsluga-hr/zgloszenia)
 *
 * CO BYŁO ŹLE: moduł widokowy nie miał własnego adresu kolejki — wchodziło się na nią adresem
 * CUDZEGO modułu z parametrem (`/obsluga-it/hr?widok=zgloszenia`). Gdy fala 2026-09-01 skasowała
 * jedyny przycisk prowadzący pod ten adres, kolejka zgłoszeń HR zniknęła użytkownikowi z aplikacji
 * całkowicie — adresu nie dało się odgadnąć, bo nie należał do modułu HR.
 *
 * Ta tabela jest JEDNYM źródłem prawdy dla trzech miejsc, które muszą mówić tym samym głosem:
 *  1. App.js — rejestruje `<Route>` kolejki dla KAŻDEGO wpisu (nowy moduł widokowy = trasa gratis),
 *  2. ItManagementPage — przekierowuje stary adres `?widok=zgloszenia` na adres z tej tabeli,
 *  3. strona wbudowana modułu — rysuje wejście (przycisk) na swoją kolejkę.
 *
 * Dokładając stronę wbudowaną (VIEW_PAGE_OPTIONS w /admin/categories) dopisz ją TUTAJ razem
 * z kluczem trasy w ROUTE_MAP — inaczej moduł dostanie ekran obsługi bez kolejki zgłoszeń.
 */
export const VIEW_MODULE_QUEUE_ROUTES = {
    hrManagement: "hrTickets",
};

/**
 * Odwrócenie VIEW_MODULE_QUEUE_ROUTES: klucz trasy kolejki → kod strony wbudowanej.
 * App.js rejestruje trasy LITERALNIE (`routePath("hrTickets")` — tak działa detektor sierot
 * w routes.test.js), ale kod modułu bierze STĄD, żeby trasa nie mogła się rozjechać z tabelą.
 */
export const VIEW_MODULE_QUEUE_VIEW_CODES = Object.fromEntries(
    Object.entries(VIEW_MODULE_QUEUE_ROUTES).map(([viewModuleCode, routeKey]) => [routeKey, viewModuleCode])
);
